"use client";

import { IProduct } from "@/types/product-d-t";
import { useState, useEffect } from "react";
import ProductItem from "./single-product/product-item";

type IProps = {
  products?: IProduct[];
  spacing?: string;
  style_2?: boolean;
  featuredProducts?: IProduct[];
};

const SaleOffProducts = ({
  products = [],
  spacing = "pb-100",
  style_2 = false,
  featuredProducts = [],
}: IProps) => {
  const [visibleCount, setVisibleCount] = useState<number>(10);
  const [windowWidth, setWindowWidth] = useState<number>(0);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSeeMore = () => {
    const total = featuredProducts.length || products.length;
    setVisibleCount(total);
  };

  const productsToShow = (featuredProducts.length > 0 ? featuredProducts : products).slice(
    0,
    visibleCount
  );

  // Determine flex-basis per row based on screen size
  const getFlexBasis = () => {
    if (windowWidth < 768) return "calc(50% - 10px)"; // mobile: 2 per row
    if (windowWidth < 1024) return "calc(33.33% - 10px)"; // tablet: 3 per row
    return "calc(20% - 10px)"; // desktop: 5 per row
  };

  return (
    <section
      className={`sale__area ${spacing}`}
      style={{ textAlign: "center", padding: "50px 0" }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Section Title */}
        <div style={{ marginBottom: "65px" }}>
          <small
            style={{
              letterSpacing: "3px",
              fontSize: "14px",
              color: "#888",
              fontWeight: 700,
              textTransform: "uppercase",
              display: "block",
              marginBottom: "8px",
            }}
          >
            Curated Collection
          </small>

          <h2
            style={{
              fontSize: windowWidth < 768 ? "34px" : "56px",
              fontWeight: 900,
              lineHeight: 1.05,
              color: "#111",
              textTransform: "capitalize",
              marginBottom: "15px",
            }}
          >
            Stylish Clothing for Modern Trends ✨
          </h2>

          <p
            style={{
              maxWidth: "540px",
              margin: "0 auto",
              color: "#555",
              fontSize: "15px",
              lineHeight: 1.6,
            }}
          >
            Explore our hand-picked collection of premium clothing — trendy,
            chic, and designed for the modern fashion lover.
          </p>
        </div>

        {/* Products Grid */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          {productsToShow.map((product: any, i: number) => {
            const firstImage = product.images?.[0]?.image || "/placeholder.png";
            const normalizedProduct = { ...product, image: firstImage };

            return (
              <div
                key={i + product?.id}
                style={{
                  flex: `0 0 ${getFlexBasis()}`,
                  maxWidth: getFlexBasis(),
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ProductItem product={normalizedProduct} />
              </div>
            );
          })}
        </div>

        {/* See More Button */}
        {visibleCount < (featuredProducts.length || products.length) && (
          <div style={{ marginTop: "35px", display: "flex", justifyContent: "center" }}>
            <button
              onClick={handleSeeMore}
              style={{
                padding: "14px 42px",
                borderRadius: "999px",
                border: "none",
                background: "linear-gradient(135deg,#000,#444)",
                color: "#fff",
                fontWeight: "800",
                letterSpacing: "1px",
                textTransform: "uppercase",
                fontSize: "13px",
                transition: "all 0.3s ease",
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 15px 40px rgba(0,0,0,0.35)";
                e.currentTarget.style.background = "linear-gradient(135deg,#111,#666)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.25)";
                e.currentTarget.style.background = "linear-gradient(135deg,#000,#444)";
              }}
            >
              Explore More Products →
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default SaleOffProducts;
